import { AccountLicencesPageModel } from './model';
import { RedirectFunction, RouterState } from 'react-router';
import { action, autorun, runInAction } from 'mobx';
import { APIController } from '../../api/controller';
import { UserModel } from '../../user/model';
import { EnvModel } from '../../env/model';
import { UIController } from '../../ui/controller';
import { stripQueryString } from '../../../lib/string';
import { isEmpty } from '../../../components/project-happy/utilities/objects';
import { BugsnagController } from '../../bugsnag/controller';
import { LicenceSortType, LicenceStatus, SortOrder } from '../../../components/project-happy/utilities/types';
import { LicenceSchema } from '../../../types/schema';
import { APIPaginatedResponse } from '../../api/types';

export class AccountLicencesPageController {
  disposables: (() => void)[] = [];

  constructor(
    private user: UserModel,
    private model: AccountLicencesPageModel,
    private api: APIController,
    private env: EnvModel,
    private ui: UIController,
    private bugsnag: BugsnagController
  ) {}

  dispose() {
    this.disposables.forEach((disposer) => typeof disposer === 'function' && disposer());
  }

  @action
  onEnter = async (nextState: RouterState, replace: RedirectFunction) => {
    try {
      this.model.loading = true;

      this.ui.setBreadcrumbs([
        {
          path: '/account',
          label: 'Account',
        },
        {
          path: '/account/licenses',
          label: 'Licenses',
        },
      ]);

      await this.ui.setSEO(nextState.location.pathname);

      await this.env.ready;

      this.disposables.push(
        autorun(() => {
          if (!isEmpty(this.user.channel)) {
            this.model.channelId = this.user.channel.id;
            this.loadLicences();
          }
        })
      );

      return;
    } catch (e) {
      replace({
        pathname: `/account/licenses`,
      });
    }

    return;
  };

  @action
  fetchLicenses = async (page = 1): Promise<APIPaginatedResponse<Array<LicenceSchema>>> => {
    try {
      const licences = await this.api.user.getLicencedTracks({
        userIdentity: this.user.user.identity,
        page,
        sortType: this.model.sortType,
        sortOrder: this.model.dateOrder,
        channelId: this.model.channelId,
        status: this.model.status,
      });

      return licences;
    } catch (error) {
      this.bugsnag.notifyException(error);
    }
  };

  @action
  loadLicences = async (): Promise<void> => {
    try {
      this.model.loading = true;

      const licences = await this.fetchLicenses();

      this.model.licences = licences.data;
      this.model.pagination = licences.meta.pagination;
    } catch (error) {
      this.bugsnag.notifyException(error);
    } finally {
      this.model.loading = false;
    }
  };

  @action
  loadMoreLicences = async (): Promise<void> => {
    if (this.model.pagination.current_page === this.model.pagination.total_pages) return;

    this.model.loadingMore = true;

    const page = this.model.pagination.current_page + 1;

    try {
      const licences = await this.fetchLicenses(page);

      this.model.licences.push(...licences.data);
      this.model.pagination = licences.meta.pagination;
    } catch (error) {
      this.bugsnag.notifyException(error);
    } finally {
      this.model.loadingMore = false;
    }
  };

  @action
  changeSortOrder = async ({
    sortType,
    sortOrder,
  }: {
    sortType: LicenceSortType;
    sortOrder: SortOrder;
  }): Promise<void> => {
    this.model.sortType = sortType;
    this.model.dateOrder = sortOrder;
    this.loadLicences();
  };

  @action
  filterByStatus = async (status: LicenceStatus): Promise<void> => {
    this.model.status = status;
    this.loadLicences();
  };
}
